import { useEffect } from 'react';
import { useChatbotContext } from '@/pages/Lab/_context/ChatbotContext';

import { useRecoilState, useRecoilValue } from 'recoil';
import { deviceUuidState, initialRequestState } from '@/store/atom';

import { useGetAIChatbotAnswer } from './useGetAIChatbotAnswer';
import { aiChatBotSessionIdState } from '@/store/atom';
import { Message } from '@/types/discover';
import { SESSION_RESET_ALERT, CHATBOT_INITIAL_MESSAGE } from '@/pages/Lab/_constants/_const';
import { RootState, useAppSelector } from '@/redux/store';
import { useLog } from './useLog';

export const useChatbotEffects = ({
  messagesEndRef,
  inputRef,
  adjustHeight,
}: {
  messagesEndRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  adjustHeight: () => void;
}) => {
  const { setIsThinking, setMessages } = useChatbotContext();
  const [sessionId] = useRecoilState(aiChatBotSessionIdState);

  const [initialRequest, setInitialRequest] = useRecoilState(initialRequestState);
  const { messages, setStoredMessages, input, isLoading, setIsLoading } = useChatbotContext();

  const { getAiChatBotAnswer } = useGetAIChatbotAnswer();
  const user = useAppSelector((state: RootState) => state.user);
  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);

  // 입력값이 변경될 때마다 높이 조정
  useEffect(() => {
    if (inputRef.current) {
      adjustHeight();
    }
  }, [input]);

  // 메시지가 추가될 때마다 스크롤 조정 및 로컬 저장
  useEffect(() => {
    if (messages.length === 0) return;

    setStoredMessages(messages);

    const isUserMessage = messages[messages.length - 1]?.sender === 'user';
    messagesEndRef.current?.scrollIntoView({ behavior: isUserMessage ? 'instant' : 'smooth' });
  }, [messages]);

  //로딩 중일 때 자동 스크롤
  useEffect(() => {
    if (isLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isLoading]);

  //  main->(a질문)->aichatbotpage 초기 요청(initialRequest)
  useEffect(() => {
    const processInitialRequest = async () => {
      const newMessage: Message = {
        id: Date.now(),
        text: initialRequest,
        sender: 'user',
        sessionId,
      };

      if (!initialRequest) return;

      const lastMessage = messages[messages.length - 1];
      const newMessages =
        lastMessage?.id !== 0
          ? [...messages, SESSION_RESET_ALERT, CHATBOT_INITIAL_MESSAGE, newMessage]
          : [...messages, newMessage];

      setMessages(newMessages);

      setIsThinking(true);

      // 첫 번째 요청 (세션 초기화)
      const response = await getAiChatBotAnswer({
        messageText: '',
        skipMessageSave: true,
        superChat: true,
      });
      // 첫 번째 요청 완료 후 isThinking 상태 해제
      setIsThinking(false);

      setIsLoading(true);
      // 두 번째 요청 (초기 질문 전송)
      await getAiChatBotAnswer({
        messageText: initialRequest,
        propsSessionId: response?.session_id ?? '',
      });
      setUserEventHistory({
        eventName: 'home_example_request_ai_chatbot',
        eventValue: initialRequest,
        deviceUuid,
      });
      setIsLoading(false);
      // 초기 요청 처리 완료 후 상태 초기화
      setInitialRequest('');
    };

    processInitialRequest();
  }, []);
};
